import type { QuizReviewResponse } from "@/client"
import { AttemptsService, UsersService } from "@/client"
import { ScoreDistributionCard } from "@/components/Attempts/ScoreDistributionCard"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { QuestionReview } from "@/components/TestTaking/QuestionReview"
import {
  ReviewQuestionNavigator,
  useNavigatorTriggerCompact,
} from "@/components/TestTaking/QuizInterface/QuestionNavigator"
import {
  type ReviewQuestionFilter,
  ReviewQuestionSummary,
} from "@/components/TestTaking/ReviewQuestionSummary"
import { StudentTimerReviewSummary } from "@/components/TestTaking/StudentTimer/StudentTimerReviewSummary"
import {
  type AttemptTimerRecord,
  readAttemptTimerRecord,
} from "@/components/TestTaking/StudentTimer/studentTimer"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerRoot,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import {
  Box,
  Button,
  Grid,
  Icon,
  IconButton,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { FaArrowLeft } from "react-icons/fa"
import { FiList } from "react-icons/fi"

export const Route = createFileRoute(
  "/_layout/quizzes/$quizId/attempts/$attemptId/review",
)({
  component: AttemptReviewPage,
})

function AttemptReviewPage() {
  const { quizId, attemptId } = Route.useParams()
  const [selectedFilter, setSelectedFilter] =
    useState<ReviewQuestionFilter>("all")
  const [summaryOpen, setSummaryOpen] = useState(false)
  const { isCompact: compactSummaryTrigger, triggerRef: summaryTriggerRef } =
    useNavigatorTriggerCompact()
  const storageKey = `peer-compare-opt-in:${attemptId}`
  const [peerCompareOptIn, setPeerCompareOptIn] = useState(
    () => localStorage.getItem(storageKey) === "true",
  )

  const handlePeerCompareChange = (checked: boolean) => {
    setPeerCompareOptIn(checked)
    if (checked) {
      localStorage.setItem(storageKey, "true")
    } else {
      localStorage.removeItem(storageKey)
    }
  }
  // Read after mount: the taking view's unmount cleanup (which records the
  // final whole-test time) runs before this effect, but after our render.
  const [timerRecord, setTimerRecord] = useState<AttemptTimerRecord | null>(
    null,
  )
  useEffect(() => {
    setTimerRecord(readAttemptTimerRecord(attemptId))
  }, [attemptId])
  const { data, isLoading, error } = useQuery<QuizReviewResponse>({
    queryKey: ["attempt-review", quizId, attemptId],
    queryFn: () =>
      AttemptsService.reviewQuizAttempt({
        quizId,
        attemptId,
      }) as Promise<QuizReviewResponse>,
  })

  const attemptReview: QuizReviewResponse | undefined = data
  const isCompleted = attemptReview?.completed !== false

  useEffect(() => {
    if (!attemptReview || !window.location.hash) return

    const anchor = decodeURIComponent(window.location.hash.slice(1))
    const question = document.getElementById(anchor)
    question?.scrollIntoView({ block: "start" })
  }, [attemptReview])

  const { data: currentUser } = useQuery({
    queryKey: ["currentUser"],
    queryFn: UsersService.readUserMe,
  })

  const isTeacher = currentUser?.is_superuser || false

  const breadcrumbs = useRouteBreadcrumbs("quizzes.index", [
    { label: attemptReview?.quiz_name ?? "Quiz", href: `/quizzes/${quizId}` },
    { label: "Attempt Review", isCurrent: true },
  ])

  const actions = (
    <Link to="/attempts-history">
      <Button variant="subtle" gap={2}>
        <FaArrowLeft />
        Back to Attempts History
      </Button>
    </Link>
  )

  if (isLoading) {
    return (
      <PageShell
        title={attemptReview?.quiz_name ?? "Attempt Review"}
        description="Loading attempt review..."
        breadcrumbs={breadcrumbs}
        actions={actions}
      >
        <PageSkeleton variant="detail" rows={4} />
      </PageShell>
    )
  }

  if (error) {
    return (
      <PageShell
        title="Attempt Review"
        description="We couldn't load this attempt review."
        breadcrumbs={breadcrumbs}
        actions={actions}
      >
        <Text color="red.500">Error: {(error as Error).message}</Text>
      </PageShell>
    )
  }

  if (!attemptReview) {
    return (
      <PageShell
        title="Attempt Review"
        description="No attempt review data found."
        breadcrumbs={breadcrumbs}
        actions={actions}
      >
        <AppEmptyState
          title={emptyStateCopy.attempts.reviewMissing.title}
          description={emptyStateCopy.attempts.reviewMissing.description}
        />
      </PageShell>
    )
  }

  const peerComparisonAvailable =
    (attemptReview.show_score_comparison ?? false) ||
    (attemptReview.show_percentile ?? false)
  const canCompareWithPeers = isCompleted && peerComparisonAvailable

  const filteredQuestions = attemptReview.questions.filter((question) => {
    if (selectedFilter === "all") return true
    const isCorrect = question.user_answer !== null && question.is_correct
    return selectedFilter === "correct" ? isCorrect : !isCorrect
  })

  return (
    <PageShell
      title={attemptReview.quiz_name}
      description={
        <Text color="fg.muted">
          {isCompleted
            ? `Review: ${attemptReview.score} / ${attemptReview.total_questions} correct`
            : "Incomplete attempt — unanswered questions are shown as not answered."}
        </Text>
      }
      breadcrumbs={breadcrumbs}
      actions={actions}
      maxW="6xl"
      py={10}
      contentGap={8}
    >
      <Stack gap={8}>
        <StudentTimerReviewSummary record={timerRecord} />
        {peerComparisonAvailable && (
          <Box>
            <Checkbox
              checked={peerCompareOptIn}
              disabled={!isCompleted}
              onCheckedChange={({ checked }) =>
                handlePeerCompareChange(Boolean(checked))
              }
            >
              Compare my score with my peers
            </Checkbox>
          </Box>
        )}
        {canCompareWithPeers && peerCompareOptIn && (
          <ScoreDistributionCard
            quizId={quizId}
            attemptId={attemptId}
            showPercentileNumber={attemptReview.show_percentile}
          />
        )}
        <Box
          ref={summaryTriggerRef}
          position="sticky"
          top="0"
          zIndex={10}
          display="flex"
          w="fit-content"
          ml="auto"
          justifyContent="flex-end"
          pointerEvents="none"
          bg="bg"
          borderRadius="md"
          p={1}
          shadow="sm"
        >
          <DrawerRoot
            placement="start"
            open={summaryOpen}
            onOpenChange={(event) => setSummaryOpen(event.open)}
          >
            <DrawerTrigger asChild>
              {compactSummaryTrigger ? (
                <IconButton
                  aria-label="Summary"
                  size="xs"
                  variant="ghost"
                  pointerEvents="auto"
                >
                  <Icon as={FiList} />
                </IconButton>
              ) : (
                <Button variant="ghost" pointerEvents="auto">
                  Summary
                </Button>
              )}
            </DrawerTrigger>
            <DrawerBackdrop />
            <DrawerContent
              maxW="320px"
              h="100dvh"
              maxH="100dvh"
              display="flex"
              flexDir="column"
              overflow="hidden"
            >
              <DrawerCloseTrigger />
              <DrawerBody flex="1" minH="0" overflowY="auto" pt={12}>
                <Stack gap={4}>
                  <ReviewQuestionSummary
                    questions={attemptReview.questions}
                    selectedFilter={selectedFilter}
                    onFilterChange={setSelectedFilter}
                  />
                  <ReviewQuestionNavigator
                    questions={attemptReview.questions}
                    questionStatuses={attemptReview.questions.map((question) =>
                      question.user_answer === null
                        ? "unanswered"
                        : question.is_correct
                          ? "correct"
                          : "incorrect",
                    )}
                    visibleQuestionIndexes={filteredQuestions.map((question) =>
                      attemptReview.questions.indexOf(question),
                    )}
                    onNavigate={(questionIndex) => {
                      document
                        .getElementById(
                          `question-${attemptReview.questions[questionIndex].question_id}`,
                        )
                        ?.scrollIntoView({ block: "start" })
                      setSummaryOpen(false)
                    }}
                  />
                </Stack>
              </DrawerBody>
            </DrawerContent>
          </DrawerRoot>
        </Box>
        <Grid templateColumns="1fr" gap={6}>
          <Stack gap={8}>
            {filteredQuestions.map((question) => (
              <Stack
                key={question.question_id}
                id={`question-${question.question_id}`}
                scrollMarginTop="6rem"
              >
                <QuestionReview
                  question={question}
                  quizId={quizId}
                  isTeacher={isTeacher}
                  questionNumber={attemptReview.questions.indexOf(question) + 1}
                  paceOvertime={
                    timerRecord?.questionOvertime[question.question_id]
                  }
                />
              </Stack>
            ))}
          </Stack>
        </Grid>
      </Stack>
    </PageShell>
  )
}
